import { el, fmtInt, fmtCompact, fmtDate } from '../ui/dom';
import { renderShell, renderError } from '../ui/shell';
import { kpiCard, kpiGrid, panel, panelGrid, segmented, caption } from '../ui/components';
import { barList } from '../ui/charts';
import {
  getAscStatus,
  getAscOverview,
  getAppStoreStats,
  getAppStoreNotifications,
  getWebOverview,
  getWebTopPages,
  getWebTrafficSources,
  getWebCtaPerformance,
  requestAppStoreTestNotification,
  resyncAppStoreConnectData,
  getAppleSearchAdsStatus,
  syncAppleSearchAdsSpend,
  getAppleAdsReport,
  upsertAppleAdsSpend,
  deleteAppleAdsSpend,
  type AscOverview,
  type AscStatus,
  type AppStoreStats,
  type AppStoreNotificationRow,
  type WebOverview,
  type WebOverviewDevice,
  type WebRow,
  type AscPeriod,
  type MarketPeriod,
  type ApproxUsdConversion,
  type AppStoreTestNotificationEnvironment,
  type AppleAdsReport,
  type AppleAdsAttributionRow,
  type AppleAdsSpendRow,
} from '../api/market';
import { UnauthorizedError, ApiError } from '../api/client';
import { navigate } from '../router';
import type { RouteContext } from '../router';
import { summarizeAiReferrals, summarizeBusinessActions } from './ai-referrals';

function loadingBox(): HTMLElement {
  return el('div', { class: 'loading' }, ['Loading…']);
}

async function settle<T>(p: Promise<T>): Promise<T | null> {
  try {
    return await p;
  } catch (err) {
    if (err instanceof UnauthorizedError) throw err;
    return null;
  }
}

function pick(
  row: WebRow,
  keys: string[],
): string | number | undefined {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null) return row[k] ?? undefined;
  }
  return undefined;
}

function labelOf(row: WebRow): string {
  return String(
    pick(row, [
      'path',
      'page',
      'source',
      'referrerDomain',
      'referrer',
      'cta_target',
      'target',
      'device_type',
      'label',
      'name',
      'campaign',
      'utmCampaign',
      'utm_source',
    ]) ?? '—',
  );
}

// Wire shape is the raw `web_analytics_*` rollup columns (snake_case) — see
// WebAnalyticsService in the API. total_views/total_clicks are the primary
// metric for each panel; unique_visitors/unique_clickers (session counts, not
// people) only get picked when the primary column is absent.
function valueOf(row: WebRow): number {
  const v = pick(row, [
    'total_views',
    'total_clicks',
    'views',
    'clicks',
    'count',
    'unique_visitors',
    'unique_clickers',
    'visitors',
    'uniqueVisitors',
    'uniqueSessions',
    'pageviews',
    'viewCount',
    'clickCount',
    'sessions',
    'total',
  ]);
  return typeof v === 'number' ? v : Number(v) || 0;
}

function webBarList(rows: WebRow[] | null): HTMLElement {
  if (!Array.isArray(rows) || rows.length === 0) {
    return el('p', { class: 'panel-empty' }, ['No data yet']);
  }
  return barList(rows.slice(0, 8).map((r) => ({ label: labelOf(r), value: valueOf(r) })));
}

// Currency comes from the webhook payload, not always USD — format per-row.
function moneyIn(amount: number, currency: string): string {
  try {
    return amount.toLocaleString('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    });
  } catch {
    return `${amount.toLocaleString('en-US')} ${currency}`;
  }
}

// Per-currency breakdown, capped at the top 3 entries by amount (descending)
// so the sub-line can't wrap forever — the remainder collapses to "+ N more".
const BREAKDOWN_CAP = 3;

function topAmounts(
  rows: { amount: number; currency: string }[] | undefined,
): { amount: number; currency: string }[] | null {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  return [...rows].sort((a, b) => b.amount - a.amount);
}

function revenueBreakdown(rows: { amount: number; currency: string }[] | undefined): string | null {
  const sorted = topAmounts(rows);
  if (!sorted) return null;
  const shown = sorted.slice(0, BREAKDOWN_CAP).map((r) => moneyIn(r.amount, r.currency));
  const rest = sorted.length - shown.length;
  return rest > 0 ? `${shown.join(' · ')} + ${rest} more` : shown.join(' · ');
}

function fmtApproxUsd(n: number): string {
  return `≈ ${n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })} USD`;
}

interface MoneyCardParts {
  primary: string;
  /** Per-currency breakdown (+ unconverted count), for the card's sub2 line. */
  sub2?: string;
  usedApprox: boolean;
  ratesAsOf?: string;
}

// Builds the (primary value, breakdown sub-line) pair for a money card per
// the pinned contract: approxUsd is the headline when available; when it's
// null (e.g. every amount is in an unrecognized currency) the per-currency
// breakdown must NEVER be promoted whole into the giant KPI primary — the
// primary collapses to a compact "N currencies" (or the lone amount+code
// when there's exactly one), with the full breakdown demoted to the muted
// sub-line, capped at the top 3 entries by amount.
function moneyCardParts(
  conversion: ApproxUsdConversion | undefined,
  amounts: { amount: number; currency: string }[] | undefined,
): MoneyCardParts | null {
  if (conversion && conversion.approxUsd !== null) {
    const extras: string[] = [];
    const breakdown = revenueBreakdown(amounts);
    if (breakdown) extras.push(breakdown);
    if (conversion.unconverted.length > 0) {
      extras.push(
        `+ ${conversion.unconverted.length} unconverted currenc${
          conversion.unconverted.length === 1 ? 'y' : 'ies'
        }`,
      );
    }
    return {
      primary: fmtApproxUsd(conversion.approxUsd),
      sub2: extras.length ? extras.join(' · ') : undefined,
      usedApprox: true,
      ratesAsOf: conversion.ratesAsOf,
    };
  }
  const sorted = topAmounts(amounts);
  if (sorted) {
    const primary =
      sorted.length === 1
        ? moneyIn(sorted[0].amount, sorted[0].currency)
        : `${sorted.length} currencies`;
    return {
      primary,
      sub2: sorted.length > 1 ? revenueBreakdown(amounts) ?? undefined : undefined,
      usedApprox: false,
    };
  }
  return null;
}

function windowLabel(period: MarketPeriod): string {
  return period === 'all' ? 'all-time' : period;
}

function periodDays(period: MarketPeriod): number | null {
  switch (period) {
    case '7d':
      return 7;
    case '30d':
      return 30;
    case '90d':
      return 90;
    case 'all':
      return null;
  }
}

// startDate for the webhook-stats calls; omitted (undefined) for 'all' so
// the API sums every notification on file.
function startDateFor(period: MarketPeriod): string | undefined {
  const days = periodDays(period);
  if (days === null) return undefined;
  return new Date(Date.now() - days * 86400000).toISOString().slice(0, 10);
}

const PRO_PRODUCT_ID = 'com.checkidapp.pro';

export async function renderMarket({ root }: RouteContext): Promise<void> {
  const content = renderShell(root, 'market');

  let period: MarketPeriod = '30d';
  // Persisted across period-driven reloads so reopening/re-filtering the
  // drill-down doesn't surprise the founder mid-verification.
  const notifState: NotifState = { open: false, filter: 'all' };

  const cap = caption('App Store performance and marketing-site traffic.');
  const rangeHost = el('div', { class: 'seg-host' });
  const head = el('div', { class: 'view-head' }, [
    el('div', { class: 'page-head' }, [el('h1', {}, ['Market'])]),
    rangeHost,
  ]);
  const body = el('div', {}, [loadingBox()]);
  content.append(head, cap, body);

  function renderRange(): void {
    rangeHost.replaceChildren(
      segmented(
        [
          { label: '7 days', value: '7d' },
          { label: '30 days', value: '30d' },
          { label: '90 days', value: '90d' },
          { label: 'All', value: 'all' },
        ],
        period,
        (v) => {
          period = v as MarketPeriod;
          renderRange();
          void load();
        },
      ),
    );
  }

  async function load(): Promise<void> {
    body.replaceChildren(loadingBox());
    try {
      const startDate = startDateFor(period);
      // ASC overview caps at 90d server-side; 'all' best-effort-requests the
      // widest supported window, but every ASC card label is driven off the
      // period the server actually echoes back (asc.period), not this local
      // request, so a silent 90d-for-"all" substitution is never mislabeled.
      const ascPeriod: AscPeriod = period === 'all' ? '90d' : period;
      const webDays = periodDays(period) ?? 3650;

      const [status, asc, appStore, proStats, appleAds, web, pages, sources, ctas] = await Promise.all([
        settle(getAscStatus()),
        settle(getAscOverview(ascPeriod)),
        settle(getAppStoreStats({ environment: 'Production', startDate })),
        settle(
          getAppStoreStats({ productId: PRO_PRODUCT_ID, environment: 'Production', startDate }),
        ),
        settle(getAppleAdsReport({ startDate })),
        settle(getWebOverview(webDays)),
        settle(getWebTopPages(webDays)),
        settle(getWebTrafficSources(webDays)),
        settle(getWebCtaPerformance(webDays)),
      ]);
      body.replaceChildren(
        appStoreSection(status, asc, appStore, proStats, period),
        appleAdsSection(appleAds, period, () => void load()),
        notificationsPanel(period, notifState),
        webSection(web, pages, sources, ctas),
      );
    } catch (err) {
      if (err instanceof UnauthorizedError) return navigate('/login');
      renderError(body, err instanceof Error ? err.message : 'Failed to load.');
    }
  }

  renderRange();
  await load();
}

// Per-metricType sync piping info from GET /status recentMetrics — answers
// "what is Apple actually delivering us" without digging into logs: the
// report names on the most recent stored day, which fields the aggregation
// populated from them, and any reports whose name/columns matched nothing.
function reportStatusPanel(status: AscStatus | null): HTMLElement | null {
  const metrics = status?.recentMetrics;
  if (!Array.isArray(metrics) || metrics.length === 0) return null;
  return el('details', { class: 'details-panel' }, [
    el('summary', {}, ['ASC report status']),
    el(
      'ul',
      { class: 'muted-list' },
      metrics.map((m) => {
        const parts: string[] = [];
        parts.push(m.mostRecentDate ? `latest ${m.mostRecentDate}` : 'no data stored');
        if (m.reportNames.length > 0) parts.push(`reports: ${m.reportNames.join(', ')}`);
        parts.push(
          m.populatedFields.length > 0
            ? `populated: ${m.populatedFields.join(', ')}`
            : 'nothing populated',
        );
        if (m.unmatchedReports.length > 0) {
          parts.push(
            `unmatched: ${m.unmatchedReports.map((u) => `${u.name} (${u.rows} rows)`).join(', ')}`,
          );
        }
        return el('li', { class: 'panel-note' }, [`${m.metricType} — ${parts.join(' · ')}`]);
      }),
    ),
  ]);
}

function appStoreConnected(status: AscStatus | null, asc: AscOverview | null): boolean {
  if (status && status.configured === false) return false;
  return !!(asc && (asc.downloads || asc.sales || asc.usage));
}

function testNotificationControl(): HTMLElement {
  let env: AppStoreTestNotificationEnvironment = 'production';
  const noteHost = el('div', {});
  const envHost = el('div', { class: 'seg-host' });
  const sendBtn = el(
    'button',
    { class: 'btn btn-ghost btn-sm', type: 'button' },
    ['Send Apple test notification'],
  );

  function setNote(msg: string, tone: 'ok' | 'error'): void {
    noteHost.replaceChildren(
      el(
        'div',
        {
          class: tone === 'error' ? 'banner banner-error' : 'banner',
          style: tone === 'ok' ? 'background:var(--ok-bg);color:var(--ok-fg)' : '',
          role: 'alert',
        },
        [msg],
      ),
    );
  }

  function renderEnv(): void {
    envHost.replaceChildren(
      segmented(
        [
          { label: 'Production', value: 'production' },
          { label: 'Sandbox', value: 'sandbox' },
        ],
        env,
        (v) => {
          env = v as AppStoreTestNotificationEnvironment;
          renderEnv();
        },
      ),
    );
  }
  renderEnv();

  sendBtn.addEventListener('click', () => {
    sendBtn.disabled = true;
    noteHost.replaceChildren();
    requestAppStoreTestNotification(env)
      .then(() => {
        setNote(
          'Requested — Apple will deliver a signed TEST notification to the webhook shortly; check Slack and the stats below.',
          'ok',
        );
      })
      .catch((err) => {
        setNote(
          err instanceof ApiError || err instanceof Error
            ? err.message
            : 'Request failed.',
          'error',
        );
      })
      .finally(() => {
        sendBtn.disabled = false;
      });
  });

  const resyncBtn = el(
    'button',
    { class: 'btn btn-ghost btn-sm', type: 'button' },
    ['Re-sync ASC data (90d)'],
  );
  resyncBtn.addEventListener('click', () => {
    resyncBtn.disabled = true;
    noteHost.replaceChildren();
    resyncAppStoreConnectData(90)
      .then(() => {
        setNote(
          'Re-sync started — data refreshes over the next few minutes; reload to see repaired figures.',
          'ok',
        );
      })
      .catch((err) => {
        setNote(
          err instanceof ApiError || err instanceof Error ? err.message : 'Request failed.',
          'error',
        );
      })
      .finally(() => {
        resyncBtn.disabled = false;
      });
  });

  // Apple Ads spend import: without this the only way to pull spend is to wait
  // for the nightly cron, and the only way to learn the credentials are wrong
  // is to notice CAC never populated.
  const spendBtn = el(
    'button',
    { class: 'btn btn-ghost btn-sm', type: 'button' },
    ['Import Apple Ads spend (30d)'],
  );
  spendBtn.addEventListener('click', () => {
    spendBtn.disabled = true;
    noteHost.replaceChildren();
    syncAppleSearchAdsSpend(30)
      .then(() => {
        setNote(
          'Import started — reload in a minute, then check Apple Ads status below for what it wrote.',
          'ok',
        );
      })
      .catch((err) => {
        setNote(
          err instanceof ApiError || err instanceof Error ? err.message : 'Request failed.',
          'error',
        );
      })
      .finally(() => {
        spendBtn.disabled = false;
      });
  });

  const statusHost = el('div', {});
  const renderSpendStatus = (): void => {
    void getAppleSearchAdsStatus()
      .then((status) => {
        if (!status.configured) {
          statusHost.replaceChildren(
            el('p', { class: 'panel-note' }, [
              `Apple Ads spend import not configured: ${
                status.errors.length ? status.errors.join(', ') : 'credentials missing'
              }. Spend stays manual-entry only.`,
            ]),
          );
          return;
        }
        // A failure explains itself here; otherwise the founder only sees
        // "0 rows" and can't tell a rejection from an empty account.
        if (status.lastError) {
          statusHost.replaceChildren(
            el('p', { class: 'panel-note' }, [
              `Apple Ads spend import last failed${
                status.lastErrorAt ? ` ${fmtDate(status.lastErrorAt)}` : ''
              }: ${status.lastError}`,
            ]),
          );
          return;
        }
        const bits: string[] = ['Apple Ads spend import configured'];
        if (status.lastSyncedAt) {
          bits.push(
            `last run ${fmtDate(status.lastSyncedAt)} — ${fmtInt(status.rowsWritten ?? 0)} day/campaign rows written`,
          );
          if (status.manualRowsSuperseded) {
            bits.push(`${fmtInt(status.manualRowsSuperseded)} hand-entered rows replaced`);
          }
          if (status.skippedUnconvertedCurrency) {
            bits.push(
              `${fmtInt(status.skippedUnconvertedCurrency)} rows skipped for an unconvertible currency`,
            );
          }
        } else {
          bits.push('no import has run yet');
        }
        statusHost.replaceChildren(el('p', { class: 'panel-note' }, [bits.join(' · ')]));
      })
      .catch(() => {
        statusHost.replaceChildren();
      });
  };
  renderSpendStatus();

  return el('div', { class: 'stack-gap' }, [
    el('div', { class: 'panel-actions' }, [envHost, sendBtn, resyncBtn, spendBtn]),
    noteHost,
    statusHost,
  ]);
}

function appStoreSection(
  status: AscStatus | null,
  asc: AscOverview | null,
  appStore: AppStoreStats | null,
  proStats: AppStoreStats | null,
  period: MarketPeriod,
): HTMLElement {
  const cards: (HTMLElement | null)[] = [];
  const windowSub = `${windowLabel(period)}, production`;
  // Tracks whether any card actually rendered an approxUsd figure this
  // render, so the "fixed rates" footnote only appears when it's relevant —
  // and carries the ratesAsOf vintage to print in it.
  let approxUsedRatesAsOf: string | undefined;

  if (appStoreConnected(status, asc) && asc) {
    // ASC's own period may differ from the requested one (it caps at 90d and
    // doesn't understand "all") — the server echoes back what it actually
    // used, so every ASC card is labeled off asc.period, never the request.
    const ascSub = asc.period ? `${asc.period} (App Store Connect)` : undefined;

    if (asc.downloads) {
      cards.push(
        kpiCard('Downloads', fmtCompact(asc.downloads.total ?? 0), {
          sub: ascSub,
          sub2:
            asc.downloads.firstTime !== undefined
              ? `${fmtInt(asc.downloads.firstTime)} first-time`
              : undefined,
        }),
      );
    }
    if (asc.sales) {
      const proceedsParts = moneyCardParts(asc.sales.approxUsdProceeds, asc.sales.totalProceeds);
      if (proceedsParts) {
        if (proceedsParts.usedApprox) approxUsedRatesAsOf = proceedsParts.ratesAsOf;
        const unitsNote =
          // Units delta, not money — never render a cross-currency proceeds
          // change as a dollar figure.
          asc.sales.unitsChange !== undefined && asc.sales.unitsChange !== null
            ? `${asc.sales.unitsChange >= 0 ? '+' : ''}${fmtInt(asc.sales.unitsChange)} units vs prior period`
            : undefined;
        cards.push(
          kpiCard('Proceeds (after Apple’s cut)', proceedsParts.primary, {
            tone: 'ok',
            sub: ascSub,
            sub2: [proceedsParts.sub2, unitsNote].filter(Boolean).join(' · ') || undefined,
          }),
        );
      }
      const iapParts = moneyCardParts(asc.sales.approxUsdIapRevenue, asc.sales.iapRevenue);
      if (iapParts) {
        if (iapParts.usedApprox) approxUsedRatesAsOf = iapParts.ratesAsOf;
        cards.push(
          kpiCard('IAP proceeds (after Apple’s cut)', iapParts.primary, {
            tone: 'ok',
            sub: ascSub,
            sub2: iapParts.sub2,
          }),
        );
      }
    }
    if (asc.usage) {
      // ASC hasn't reliably delivered the activeDevices figure (see status
      // panel below) — a confident "0" reads as "nobody's using the app",
      // which is misleading when sessions are clearly nonzero. Only render a
      // real 0 when sessions are also 0 (a plausible true zero-activity day).
      const devices = asc.usage.activeDevices;
      const sessions = asc.usage.sessions;
      const notDelivered = (devices === undefined || devices === 0) && (sessions ?? 0) > 0;
      cards.push(
        kpiCard('Active devices', notDelivered ? '—' : fmtCompact(devices ?? 0), {
          sub: ascSub,
          sub2: notDelivered
            ? 'not delivered by ASC yet — see status'
            : sessions !== undefined
              ? `${fmtCompact(sessions)} sessions`
              : undefined,
        }),
      );
    }
  }

  // Webhook-derived purchase stats work without ASC API keys. Scoped to
  // Production only — see the sandbox-exclusion note below.
  if (appStore) {
    if (appStore.totalPurchases !== undefined) {
      cards.push(kpiCard('Purchases', fmtInt(appStore.totalPurchases), { sub: windowSub }));
    }
    const revenueParts = moneyCardParts(appStore.totalRevenueApproxUsd, appStore.totalRevenue);
    if (revenueParts) {
      if (revenueParts.usedApprox) approxUsedRatesAsOf = revenueParts.ratesAsOf;
      cards.push(
        kpiCard('Webhook-reported spend', revenueParts.primary, {
          tone: 'ok',
          sub: windowSub,
          sub2: [
            'all products, before Apple’s cut — partial coverage',
            revenueParts.sub2,
          ]
            .filter(Boolean)
            .join(' · '),
        }),
      );
    }
    if (appStore.totalRefunds !== undefined) {
      cards.push(
        kpiCard('Refunds', fmtInt(appStore.totalRefunds), {
          tone: appStore.totalRefunds > 0 ? 'warn' : 'default',
          sub: windowSub,
        }),
      );
    }
  }

  const proParts = moneyCardParts(proStats?.totalRevenueApproxUsd, proStats?.totalRevenue);
  if (proParts) {
    if (proParts.usedApprox) approxUsedRatesAsOf = proParts.ratesAsOf;
    const purchasesNote =
      proStats?.totalPurchases !== undefined
        ? `${fmtInt(proStats.totalPurchases)} purchases`
        : undefined;
    cards.push(
      kpiCard('Pro (one-time) — webhook-derived', proParts.primary, {
        tone: 'ok',
        sub: windowSub,
        sub2: [proParts.sub2, purchasesNote].filter(Boolean).join(' · ') || undefined,
      }),
    );
  }

  const present = cards.filter((c): c is HTMLElement => !!c);
  const bodyEl =
    present.length > 0
      ? kpiGrid(present)
      : el('div', { class: 'empty' }, [
          'App Store Connect isn’t connected. Add ASC API keys (App Store Connect → Integrations) to see downloads, proceeds, and usage here. Webhook purchase stats appear once purchases occur.',
        ]);

  const invalidCurrencyRows = asc?.sales?.invalidCurrencyRows ?? 0;

  return el('section', { class: 'stack-gap' }, [
    el('div', { class: 'section-head' }, [el('h2', {}, ['App Store'])]),
    testNotificationControl(),
    el('p', { class: 'panel-empty' }, ['Sandbox activity is excluded from all revenue figures.']),
    el('p', { class: 'panel-note' }, [
      'Webhook-reported spend counts only purchases Apple has notified this server about — a live pulse, not the ledger. ASC Proceeds (Apple’s settlement reports, after its commission) is the authoritative money figure; the two will differ.',
    ]),
    reportStatusPanel(status),
    invalidCurrencyRows > 0
      ? el('p', { class: 'panel-note' }, [
          `${fmtInt(invalidCurrencyRows)} row${invalidCurrencyRows === 1 ? '' : 's'} with unrecognized currency excluded — re-sync App Store Connect data to repair`,
        ])
      : null,
    bodyEl,
    approxUsedRatesAsOf
      ? el('p', { class: 'panel-note' }, [
          `USD figures are approximate, converted at fixed rates (as of ${approxUsedRatesAsOf}).`,
        ])
      : null,
  ]);
}

// ── Apple Ads attribution + paid-customer CAC ─────────────────────────────

function fmtRate(value: number | null): string {
  return value === null ? '—' : `${(value * 100).toFixed(1)}%`;
}

function appleAdsSection(
  report: AppleAdsReport | null,
  period: MarketPeriod,
  onChanged: () => void,
): HTMLElement {
  const heading = el('div', { class: 'section-head' }, [
    el('h2', {}, ['Apple Ads attribution']),
  ]);

  if (!report) {
    return el('section', { class: 'stack-gap' }, [
      heading,
      el('p', { class: 'panel-empty' }, [
        'Attribution reporting is unavailable until the API migration and app release are deployed.',
      ]),
    ]);
  }

  const s = report.summary;
  const cac = s.paidCustomerCacUsd === null ? '—' : moneyIn(s.paidCustomerCacUsd, 'USD');
  const cpa = s.installCpaUsd === null ? '—' : moneyIn(s.installCpaUsd, 'USD');
  // Ad spend is entered by hand, so an empty cost figure usually means nobody
  // has logged spend yet — say that rather than leaving a bare dash.
  const noSpendNote =
    s.spendLogged === false ? 'no ad spend logged for this window' : undefined;
  const revenue = s.attributedGrossRevenue.approxUsd;
  const cards = kpiGrid([
    kpiCard('Apple Ads spend', moneyIn(s.spendUsd, 'USD'), {
      sub: `${windowLabel(period)}, manually entered`,
    }),
    kpiCard('Attributed installs', fmtInt(s.attributedInstalls), {
      sub: `${fmtInt(s.capturedInstalls)} AdServices responses`,
      sub2: `${fmtRate(s.attributionRate)} matched Apple Ads`,
    }),
    kpiCard('Attributed paid customers', fmtInt(s.attributedPaidCustomers), {
      tone: s.attributedPaidCustomers > 0 ? 'ok' : 'default',
      sub: `${fmtInt(s.verifiedPaidCustomers)} verified customers in install cohort`,
      sub2: `${fmtRate(s.paidCustomerAttributionRate)} attributed · ${fmtRate(s.attributedInstallToPaidRate)} install → paid`,
    }),
    kpiCard('Paid-customer CAC', cac, {
      tone: s.paidCustomerCacUsd !== null ? 'ok' : 'default',
      sub: 'spend ÷ attributed paid customers',
      sub2: noSpendNote,
    }),
    kpiCard('Attributed install CPA', cpa, {
      sub: 'spend ÷ attributed installs',
      sub2: noSpendNote,
    }),
    kpiCard('Attributed gross revenue', revenue === null ? '—' : fmtApproxUsd(revenue), {
      sub: 'before Apple’s cut',
      sub2:
        s.grossRoas === null
          ? 'ROAS appears after spend and revenue arrive'
          : `${s.grossRoas.toFixed(2)}× gross ROAS`,
    }),
  ]);

  const unconverted = s.attributedGrossRevenue.unconverted;
  const notes: HTMLElement[] = [
    el('p', { class: 'panel-note' }, [
      'CAC is cohort based: spend and installs come from the selected acquisition window, while signed Production App Store Server Notifications verify paid conversions from that install cohort through today. Recent cohorts will mature as customers convert.',
    ]),
  ];
  if (unconverted.length > 0) {
    notes.push(
      el('p', { class: 'panel-note' }, [
        `Gross revenue excludes unconverted currencies: ${unconverted.join(', ')}.`,
      ]),
    );
  }

  return el('section', { class: 'stack-gap' }, [
    heading,
    cards,
    ...notes,
    panelGrid([
      appleAdsCampaignPanel(report),
      appleAdsSpendPanel(report.spend, onChanged),
    ]),
    appleAdsAttributionPanel(report.recentAttributions),
  ]);
}

function appleAdsCampaignPanel(report: AppleAdsReport): HTMLElement {
  if (report.campaigns.length === 0) {
    return panel(
      'Campaign performance',
      [el('p', { class: 'panel-empty' }, ['No attributed campaigns in this window yet.'])],
      { subtitle: 'IDs come directly from AdServices' },
    );
  }
  const rows = report.campaigns.map((campaign) =>
    el('tr', {}, [
      el('td', {}, [campaign.campaignId === 'unknown' ? 'Unavailable' : campaign.campaignId]),
      el('td', {}, [campaign.campaignName ?? '—']),
      el('td', { class: 'num' }, [fmtInt(campaign.installCount)]),
      el('td', { class: 'num' }, [fmtInt(campaign.paidCustomers)]),
      el('td', { class: 'num' }, [moneyIn(campaign.spendUsd, 'USD')]),
      el('td', { class: 'num' }, [
        campaign.paidCustomerCacUsd === null
          ? '—'
          : moneyIn(campaign.paidCustomerCacUsd, 'USD'),
      ]),
    ]),
  );
  return panel(
    'Campaign performance',
    [
      el('div', { class: 'table-wrap' }, [
        el('table', { class: 'data-table' }, [
          el('thead', {}, [
            el('tr', {}, [
              el('th', {}, ['Campaign ID']),
              el('th', {}, ['Name']),
              el('th', {}, ['Installs']),
              el('th', {}, ['Paid']),
              el('th', {}, ['Spend']),
              el('th', {}, ['CAC']),
            ]),
          ]),
          el('tbody', {}, rows),
        ]),
      ]),
    ],
    { subtitle: 'Install cohort and spend from the selected window' },
  );
}

function labeledControl(label: string, control: HTMLElement): HTMLElement {
  return el('label', { class: 'attribution-field' }, [
    el('span', { class: 'panel-note' }, [label]),
    control,
  ]);
}

function appleAdsSpendPanel(rows: AppleAdsSpendRow[], onChanged: () => void): HTMLElement {
  const dateInput = el('input', {
    class: 'control',
    type: 'date',
    value: new Date().toISOString().slice(0, 10),
    required: true,
  });
  const amountInput = el('input', {
    class: 'control',
    type: 'number',
    min: '0',
    step: '0.01',
    placeholder: 'USD',
    required: true,
  });
  const campaignInput = el('input', {
    class: 'control',
    type: 'text',
    inputMode: 'numeric',
    value: rows[0]?.campaignId ?? '2143489802',
  });
  const campaignNameInput = el('input', {
    class: 'control',
    type: 'text',
    value: rows[0]?.campaignName ?? 'AI Search Ads',
  });
  const notesInput = el('input', {
    class: 'control',
    type: 'text',
    placeholder: 'Optional note',
  });
  const status = el('div', {});
  const save = el('button', { class: 'btn btn-primary btn-sm', type: 'submit' }, [
    'Save daily spend',
  ]);
  const form = el('form', { class: 'attribution-spend-form' }, [
    labeledControl('UTC date', dateInput),
    labeledControl('Spend (USD)', amountInput),
    labeledControl('Campaign ID', campaignInput),
    labeledControl('Campaign name', campaignNameInput),
    labeledControl('Note', notesInput),
    save,
  ]);

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const amount = amountInput.valueAsNumber;
    if (!dateInput.value || !Number.isFinite(amount)) return;
    save.disabled = true;
    status.replaceChildren();
    upsertAppleAdsSpend({
      date: dateInput.value,
      amountUsd: amount,
      campaignId: campaignInput.value.trim() || undefined,
      campaignName: campaignNameInput.value.trim() || undefined,
      notes: notesInput.value.trim() || undefined,
    })
      .then(() => onChanged())
      .catch((err) => {
        status.replaceChildren(
          el('div', { class: 'banner banner-error', role: 'alert' }, [
            err instanceof Error ? err.message : 'Could not save spend.',
          ]),
        );
      })
      .finally(() => {
        save.disabled = false;
      });
  });

  const body: HTMLElement[] = [form, status];
  if (rows.length === 0) {
    body.push(
      el('p', { class: 'panel-empty' }, [
        'No spend entered for this window. CAC and CPA remain blank until spend is present.',
      ]),
    );
  } else {
    body.push(appleAdsSpendTable(rows, onChanged));
  }
  return panel('Daily ad spend', body, {
    subtitle: 'AdServices has no cost field; enter Apple Ads spend here',
  });
}

function appleAdsSpendTable(rows: AppleAdsSpendRow[], onChanged: () => void): HTMLElement {
  const body = rows.map((row) => {
    const remove = el('button', { class: 'btn btn-ghost btn-sm', type: 'button' }, ['Delete']);
    remove.addEventListener('click', () => {
      if (!window.confirm(`Delete ${row.date} spend entry?`)) return;
      remove.disabled = true;
      deleteAppleAdsSpend(row.id)
        .then(() => onChanged())
        .catch(() => {
          remove.disabled = false;
        });
    });
    return el('tr', {}, [
      el('td', {}, [row.date]),
      el('td', {}, [row.campaignName ?? row.campaignId ?? 'Unallocated']),
      el('td', { class: 'num' }, [moneyIn(row.amountUsd, 'USD')]),
      el('td', {}, [remove]),
    ]);
  });
  return el('div', { class: 'table-wrap' }, [
    el('table', { class: 'data-table' }, [
      el('thead', {}, [
        el('tr', {}, [
          el('th', {}, ['Date']),
          el('th', {}, ['Campaign']),
          el('th', {}, ['Spend']),
          el('th', {}, ['']),
        ]),
      ]),
      el('tbody', {}, body),
    ]),
  ]);
}

function appleAdsAttributionPanel(rows: AppleAdsAttributionRow[]): HTMLElement {
  if (rows.length === 0) {
    return panel(
      'Recent AdServices records',
      [el('p', { class: 'panel-empty' }, ['No app installs have reported attribution yet.'])],
      { wide: true },
    );
  }
  const body = rows.map((row) =>
    el('tr', {}, [
      el('td', {}, [fmtDate(row.firstReceivedAt)]),
      el('td', {}, [row.attribution ? 'Attributed' : 'Organic / no match']),
      el('td', {}, [row.campaignId ?? '—']),
      el('td', {}, [row.adGroupId ?? '—']),
      el('td', {}, [row.keywordId ?? 'Search Match / —']),
      el('td', {}, [row.adId ?? '—']),
      el('td', {}, [row.claimType ?? '—']),
      el('td', {}, [row.conversionType ?? '—']),
      el('td', {}, [row.supplyPlacement ?? '—']),
      el('td', {}, [
        row.clickDate
          ? `Click ${fmtDate(row.clickDate)}`
          : row.impressionDate
            ? `View ${fmtDate(row.impressionDate)}`
            : '—',
      ]),
      el('td', {}, [row.countryOrRegion ?? '—']),
      el('td', {}, [
        [row.appVersion, row.buildNumber ? `(${row.buildNumber})` : null]
          .filter(Boolean)
          .join(' ') || '—',
      ]),
      el('td', {}, [
        row.paidCustomer
          ? `Paid${row.productId ? ` · ${row.productId}` : ''}${row.purchaseDate ? ` · ${fmtDate(row.purchaseDate)}` : ''}`
          : 'Not yet',
      ]),
    ]),
  );
  return panel(
    'Recent AdServices records',
    [
      el('div', { class: 'table-wrap' }, [
        el('table', { class: 'data-table' }, [
          el('thead', {}, [
            el('tr', {}, [
              el('th', {}, ['Captured']),
              el('th', {}, ['Match']),
              el('th', {}, ['Campaign']),
              el('th', {}, ['Ad group']),
              el('th', {}, ['Keyword']),
              el('th', {}, ['Ad']),
              el('th', {}, ['Claim']),
              el('th', {}, ['Conversion']),
              el('th', {}, ['Placement']),
              el('th', {}, ['Ad event']),
              el('th', {}, ['Country']),
              el('th', {}, ['App build']),
              el('th', {}, ['Customer']),
            ]),
          ]),
          el('tbody', {}, body),
        ]),
      ]),
    ],
    {
      subtitle: 'Latest 100 install-scoped records; Search Match may omit keyword ID',
      wide: true,
    },
  );
}

// ── Verifiability drill-down: raw notifications behind the KPI cards ──────

interface NotifState {
  open: boolean;
  filter: 'all' | 'purchases' | 'refunds' | 'test';
}

// Category → underlying notificationType values. The list endpoint only
// filters on a single exact notificationType (see AdminAppStoreController /
// NotificationQueryDto), so multi-type categories are fetched one type at a
// time per page and merged client-side, newest first.
const NOTIFICATION_CATEGORY_TYPES: Record<Exclude<NotifState['filter'], 'all'>, string[]> = {
  purchases: ['SUBSCRIBED', 'DID_RENEW', 'ONE_TIME_CHARGE', 'OFFER_REDEEMED'],
  refunds: ['REFUND', 'REVOKE'],
  test: ['TEST'],
};

function notificationsPanel(period: MarketPeriod, state: NotifState): HTMLElement {
  const startDate = startDateFor(period);
  const environment = 'Production';

  const details = el('details', { class: 'details-panel' });
  details.open = state.open;

  const chipHost = el('div', { class: 'seg-host' });
  const resultsHost = el('div', { class: 'stack-gap' });
  details.append(
    el('summary', {}, ['Inspect raw notifications']),
    el('div', { class: 'stack-gap' }, [
      el('p', { class: 'panel-note' }, [
        `Backs the counts above row by row — same window (${windowLabel(period)}) and environment (production).`,
      ]),
      chipHost,
      resultsHost,
    ]),
  );

  // One page cursor per underlying notificationType (or '*' for "All").
  const cursors: Record<string, { page: number; exhausted: boolean }> = {};
  let rows: AppStoreNotificationRow[] = [];
  let loading = false;
  let errorMsg: string | null = null;

  function typesFor(): string[] | null {
    return state.filter === 'all' ? null : NOTIFICATION_CATEGORY_TYPES[state.filter];
  }

  function moreAvailable(): boolean {
    const types = typesFor();
    if (!types) return !cursors['*']?.exhausted;
    return types.some((t) => !cursors[t]?.exhausted);
  }

  async function fetchNextBatch(): Promise<AppStoreNotificationRow[]> {
    const types = typesFor();
    if (!types) {
      const page = (cursors['*']?.page ?? 0) + 1;
      const res = await getAppStoreNotifications({
        page,
        limit: 20,
        environment,
        startDate,
      });
      cursors['*'] = { page, exhausted: !res.meta.hasNextPage };
      return res.data;
    }
    const perTypeLimit = types.length > 1 ? 10 : 20;
    const batches = await Promise.all(
      types.map(async (t) => {
        const cur = cursors[t];
        if (cur?.exhausted) return [] as AppStoreNotificationRow[];
        const page = (cur?.page ?? 0) + 1;
        const res = await getAppStoreNotifications({
          page,
          limit: perTypeLimit,
          environment,
          startDate,
          notificationType: t,
        });
        cursors[t] = { page, exhausted: !res.meta.hasNextPage };
        return res.data;
      }),
    );
    return batches.flat().sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }

  function renderResults(): void {
    const children: HTMLElement[] = [];
    if (rows.length > 0) {
      children.push(notificationsTable(rows));
    } else if (!loading && !errorMsg) {
      children.push(el('p', { class: 'panel-empty' }, ['No notifications in this window.']));
    }
    if (errorMsg) {
      children.push(el('p', { class: 'panel-empty' }, [errorMsg]));
    }
    if (loading) {
      children.push(loadingBox());
    } else if (rows.length > 0 && moreAvailable()) {
      children.push(
        el('div', { class: 'pagination' }, [
          el(
            'button',
            { class: 'btn btn-ghost btn-sm', type: 'button', onClick: () => void loadMore() },
            ['Load more'],
          ),
        ]),
      );
    }
    resultsHost.replaceChildren(...children);
  }

  async function loadMore(): Promise<void> {
    loading = true;
    errorMsg = null;
    renderResults();
    try {
      const batch = await fetchNextBatch();
      rows = rows.concat(batch);
    } catch (err) {
      errorMsg =
        err instanceof ApiError || err instanceof Error
          ? err.message
          : 'Failed to load notifications.';
    } finally {
      loading = false;
      renderResults();
    }
  }

  function resetAndLoad(): void {
    rows = [];
    for (const k of Object.keys(cursors)) delete cursors[k];
    void loadMore();
  }

  function renderChips(): void {
    chipHost.replaceChildren(
      segmented(
        [
          { label: 'All', value: 'all' },
          { label: 'Purchases', value: 'purchases' },
          { label: 'Refunds', value: 'refunds' },
          { label: 'Test', value: 'test' },
        ],
        state.filter,
        (v) => {
          state.filter = v as NotifState['filter'];
          renderChips();
          resetAndLoad();
        },
      ),
    );
  }

  renderChips();
  if (state.open) {
    void loadMore();
  } else {
    resultsHost.replaceChildren(
      el('p', { class: 'panel-empty' }, ['Expand to load raw notifications.']),
    );
  }

  details.addEventListener('toggle', () => {
    state.open = details.open;
    if (state.open && rows.length === 0 && !loading) void loadMore();
  });

  return details;
}

function notificationsTable(rows: AppStoreNotificationRow[]): HTMLElement {
  const thead = el('thead', {}, [
    el('tr', {}, [
      el('th', {}, ['Date']),
      el('th', {}, ['Type']),
      el('th', {}, ['Product']),
      el('th', {}, ['Price']),
      el('th', {}, ['Environment']),
    ]),
  ]);
  const body = rows.map((r) =>
    el('tr', {}, [
      el('td', {}, [fmtDate(r.createdAt)]),
      el('td', {}, [r.subtype ? `${r.notificationType} (${r.subtype})` : r.notificationType]),
      el('td', { class: 'cell-muted' }, [r.productId ?? '—']),
      el('td', { class: 'num' }, [
        r.price !== null && r.currency ? moneyIn(r.price / 1000, r.currency) : '—',
      ]),
      el('td', {}, [r.environment]),
    ]),
  );
  return el('div', { class: 'table-wrap' }, [
    el('table', { class: 'data-table' }, [thead, el('tbody', {}, body)]),
  ]);
}

function fmtSecondsDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '0s';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function deviceBarList(devices: WebOverviewDevice[] | undefined): HTMLElement | null {
  if (!Array.isArray(devices) || devices.length === 0) return null;
  return barList(devices.map((d) => ({ label: d.device_type || '—', value: d.count })));
}

// The marketing-site KPI/traffic actually present this range — has real
// traffic occurred, not just "did the object key exist" (the old bug: it
// checked camelCase fields that never existed on the wire, so this was
// always false even with real data).
function hasWebTraffic(web: WebOverview | null): web is WebOverview {
  return !!web && (web.total_views > 0 || web.unique_visitors > 0);
}

function webSection(
  web: WebOverview | null,
  pages: WebRow[] | null,
  sources: WebRow[] | null,
  ctas: WebRow[] | null,
): HTMLElement {
  const ai = summarizeAiReferrals(sources);
  const business = summarizeBusinessActions(ctas);
  const panels: (HTMLElement | null)[] = [
    panel('AI referrals', [
      ai.kind === 'unavailable'
        ? el('p', { class: 'panel-empty' }, ['Traffic sources could not be loaded. Try another date range or reload.'])
        : ai.providers.length > 0
          ? barList(ai.providers)
          : el('p', { class: 'panel-empty' }, ['No recognized AI referrers among the returned top 20 sources.']),
      el('p', { class: 'muted small' }, [
        ai.kind === 'available' ? `${fmtInt(ai.pageviews)} recorded pageviews from AI referrers in the top 20 sources. ` : '',
        'Referrers can be omitted by apps. These counts are not unique people, installs or purchases.',
      ]),
    ], { subtitle: 'ChatGPT, Claude and other recognized assistant domains' }),
    panel('Business evaluation actions', [
      business.kind === 'unavailable'
        ? el('p', { class: 'panel-empty' }, ['CTA counts could not be loaded. Try another date range or reload.'])
        : business.actions.length > 0
          ? barList(business.actions)
          : el('p', { class: 'panel-empty' }, ['No matching business actions among the returned top 50 CTA rows.']),
      el('p', { class: 'muted small' }, ['Clicks from all traffic, limited to the top 50 CTA rows. Not attributed to AI referrals; opening an inquiry is not submitting one, and an App Store click is not a purchase.']),
    ], { subtitle: 'Teams-page interest, sample reports and App Store clicks' }),
    panel('Top pages', [webBarList(pages)]),
    panel('Traffic sources', [webBarList(sources)]),
    panel('CTA performance', [webBarList(ctas)]),
  ];

  const traffic = hasWebTraffic(web);
  const deviceBars = traffic ? deviceBarList(web.devices) : null;
  if (deviceBars) panels.push(panel('Devices', [deviceBars]));

  let kpiRow: HTMLElement;
  if (traffic) {
    const engagementPct = Number(web.engagement_rate);
    kpiRow = kpiGrid([
      kpiCard('Pageviews', fmtCompact(web.total_views)),
      kpiCard('Sessions', fmtCompact(web.unique_visitors), {
        sub: '(distinct sessions)',
      }),
      kpiCard('Avg time on page', fmtSecondsDuration(web.avg_duration_seconds)),
      kpiCard(
        'Engagement rate',
        Number.isFinite(engagementPct) ? `${engagementPct.toFixed(0)}%` : '—',
      ),
    ]);
  } else {
    kpiRow = el('p', { class: 'panel-empty' }, [
      'No marketing-site traffic in this range yet.',
    ]);
  }

  return el('section', { class: 'stack-gap' }, [
    el('div', { class: 'section-head' }, [el('h2', {}, ['Marketing site'])]),
    kpiRow,
    panelGrid(panels.filter((p): p is HTMLElement => !!p)),
  ]);
}
